import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from "@azure/msal-browser";

/**
 * Auth config delivered by app-web's anonymous /feature-flags payload (the
 * `entraExternalId` block). The SPA reads it at bootstrap, before any
 * authenticated call, to decide between the Entra External ID MSAL flow and the
 * legacy cookie flow, and to configure MSAL.
 */
/** A federated identity provider resolved for an email by server-side home-realm discovery. */
export interface FederatedProvider {
  label: string;
  domainHint: string;
}

export interface EntraAuthConfig {
  enabled: boolean;
  authority: string;
  clientId: string;
  scopes: string[];
}

export interface InteractiveLoginOptions {
  loginHint?: string;
  domainHint?: string;
  state?: string;
}

let msalInstance: PublicClientApplication | null = null;
let authConfig: EntraAuthConfig | null = null;

/**
 * Extracts the Entra auth config from the /feature-flags payload. Returns null
 * when the block is absent (host not configured, or SPA client id still empty)
 * or the flag is off, in which case the SPA stays on the legacy cookie flow.
 */
export function readEntraConfig(options: unknown): EntraAuthConfig | null {
  const block = (options as { entraExternalId?: EntraAuthConfig } | null | undefined)?.entraExternalId;
  if (!block || block.enabled !== true || !block.clientId || !block.authority || !block.scopes?.length) {
    return null;
  }
  return block;
}

/** Whether the Entra MSAL flow is active for this session. */
export function isEntraAuthActive(): boolean {
  return msalInstance !== null && authConfig !== null;
}

export function getAuthScopes(): string[] {
  return authConfig?.scopes ?? [];
}

/**
 * Initializes MSAL from the feature-flags config and processes any redirect
 * response left by a completed sign-in. Must run before the router mounts and
 * before any authenticated API call. No-op returning false when Entra auth is
 * not active, so the caller falls back to the cookie flow.
 */
export async function initializeEntraAuth(options: unknown): Promise<boolean> {
  const config = readEntraConfig(options);
  if (!config) {
    return false;
  }

  const msalConfig: Configuration = {
    auth: {
      clientId: config.clientId,
      authority: config.authority,
      knownAuthorities: [new URL(config.authority).host],
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: "localStorage",
    },
  };

  const instance = new PublicClientApplication(msalConfig);
  await instance.initialize();

  const redirectResult = await instance.handleRedirectPromise();
  if (redirectResult?.account) {
    instance.setActiveAccount(redirectResult.account);
  } else if (!instance.getActiveAccount()) {
    const [firstAccount] = instance.getAllAccounts();
    if (firstAccount) {
      instance.setActiveAccount(firstAccount);
    }
  }

  msalInstance = instance;
  authConfig = config;
  return true;
}

export function getActiveAccount(): AccountInfo | null {
  return msalInstance?.getActiveAccount() ?? null;
}

/**
 * Silently acquires an app-web access token for the active account. Returns null
 * when there is no account or a token cannot be obtained without interaction, so
 * the caller can escalate to an interactive sign-in.
 */
export async function acquireAccessToken(): Promise<string | null> {
  if (!msalInstance || !authConfig) {
    return null;
  }

  const account = msalInstance.getActiveAccount();
  if (!account) {
    return null;
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: authConfig.scopes,
      account,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return null;
    }
    throw error;
  }
}

/** Server response for POST /sign-in-realm. */
interface SignInRealmResponse {
  federated: boolean;
  provider?: FederatedProvider;
}

/**
 * Server-side home-realm discovery (GEN-7686): asks app-web whether a single typed email signs in through
 * a federated provider (and which) or as a local account. Replaces the client-side matcher that read a
 * federated-domain list off /feature-flags - that list is no longer exposed on the anonymous endpoint.
 * Returns the provider to accelerate to (its domainHint is the domain_hint, the typed email the
 * login_hint), or null for a local (password) account or on any error (fail closed to the password flow).
 */
export async function resolveSignInRealm(email: string): Promise<FederatedProvider | null> {
  const normalized = email.trim();
  if (!normalized) {
    return null;
  }

  try {
    const response = await fetch("/sign-in-realm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: normalized }),
    });
    if (!response.ok) {
      return null;
    }
    const realm = (await response.json()) as SignInRealmResponse;
    return realm.federated && realm.provider ? realm.provider : null;
  } catch {
    return null;
  }
}

/** Starts an interactive sign-in via full-page redirect. */
export async function loginInteractive(options: InteractiveLoginOptions = {}): Promise<void> {
  if (!msalInstance || !authConfig) {
    return;
  }

  await msalInstance.loginRedirect({
    scopes: authConfig.scopes,
    loginHint: options.loginHint,
    domainHint: options.domainHint,
    state: options.state,
  });
}

/** Signs the active account out via full-page redirect. */
export async function logoutRedirect(): Promise<void> {
  if (!msalInstance) {
    return;
  }

  await msalInstance.logoutRedirect({
    account: msalInstance.getActiveAccount() ?? undefined,
  });
}
